import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Camera, ShoppingCart, ArrowLeft, Check, X, Copy, ShieldAlert, Package, Image as ImageIcon, Grid3x3, Download, Play, Clock, AlertTriangle, ChevronLeft, ChevronRight, ZoomIn, RotateCcw, Lock } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useAntiScreenshot } from '../hooks/useAntiScreenshot';
import { buildPixPayload } from '../lib/pix';
import { useToast } from '../hooks/use-toast';
import { albumAPI } from '../lib/api';
import { timeUntil, formatRemaining } from '../lib/time';
import WatermarkOverlay from '../components/WatermarkOverlay';

export default function Album() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [mode, setMode] = useState('individual'); // individual | package | full
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [orderPending, setOrderPending] = useState(false); // pedido enviado, aguardando fotógrafo
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const { attackDetected } = useAntiScreenshot(true);

  // Auto-refresh album every 15s while pending order (so client sees release quickly)
  useEffect(() => {
    if (!orderPending || !code) return;
    const t = setInterval(() => {
      albumAPI.get(code).then(a => setAlbum(a)).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [orderPending, code]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    albumAPI.get(code)
      .then(a => { if (!cancelled) { setAlbum(a); setLoading(false); } })
      .catch(() => { if (!cancelled) { setAlbum(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [code]);

  const releasedIds = useMemo(() => new Set(album?.releasedPhotoIds || []), [album]);
  const paid = releasedIds.size > 0;

 const totals = useMemo(() => {
  if (!album) {
    return { count: 0, total: 0 };
  }

  // Considera somente fotos ainda não compradas
  const newPhotoIds = Array.from(selected).filter(
    (id) => !releasedIds.has(id)
  );

  const count = newPhotoIds.length;

  const pricePerPhoto = Math.max(
    0,
    Number(album.pricePerPhoto) || 0
  );

  const packageCount = Math.max(
    0,
    Number(album.packageCount) || 0
  );

  const packagePrice = Math.max(
    0,
    Number(album.packagePrice) || 0
  );

  const extraPhotoPrice =
    album.extraPhotoPrice !== null &&
    album.extraPhotoPrice !== undefined &&
    album.extraPhotoPrice !== ""
      ? Math.max(0, Number(album.extraPhotoPrice) || 0)
      : pricePerPhoto;

  const albumPrice = Math.max(
    0,
    Number(album.albumPrice) || 0
  );

  let total = 0;

  if (mode === "full") {
    total = albumPrice;
  } else if (packageCount > 0 && count >= packageCount) {
    total =
      packagePrice +
      (count - packageCount) * extraPhotoPrice;
  } else {
    total = count * pricePerPhoto;
  }

  return { count, total };
}, [album, selected, releasedIds, mode]);

  if (loading) {
    return (
      <div className="grain min-h-screen flex items-center justify-center p-6">
        <div className="text-center text-[#a89e87]">Carregando álbum…</div>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="grain min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-[#f2ecdf] mb-3">Álbum não encontrado</h1>
          <p className="text-[#a89e87] mb-6">O link informado não existe ou foi removido.</p>
          <Link to="/" className="btn-gold px-4 py-2 rounded-md text-sm font-medium inline-block">Ir para o início</Link>
        </div>
      </div>
    );
  }

  const owner = { name: album.ownerName || 'AXSELL', pixKey: album.ownerPixKey || '' };
  // NEVER fall back to a fake key — an invalid Pix destination would silently
  // route the client's payment nowhere. Empty pixKey => the checkout modal
  // will render "Pix ainda não configurado" instead of a bogus QR code.
  const pixKey = (owner.pixKey || '').trim();
  const photos = album?.photos || [];

  const toggle = (id) => {
  if (releasedIds.has(id)) return;

  setSelected((previous) => {
    const next = new Set(previous);

    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }

    return next;
  });
};

  const canCheckout = mode === 'full' || totals.count > 0;

  const confirmPayment = async () => {
    const count = mode === 'full' ? photos.length : totals.count;
    const photoIds = mode === 'full' ? photos.map(p => p.id) : Array.from(selected);
    try {
      await albumAPI.recordSale(album.code, totals.total, count, photoIds, mode);
      setOrderPending(true);
      
      toast({ title: 'Pedido enviado!', description: 'Aguardando o fotógrafo liberar seu download.' });
    } catch (e) {
      toast({ title: 'Erro ao enviar pedido', description: e?.response?.data?.detail || 'Tente novamente' });
    }
  };

  const downloadPhoto = async (photo) => {
    try {
      const resp = await fetch(photo.url, { mode: 'cors' });
      if (!resp.ok) throw new Error('Falha ao baixar');
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = photo.name || `foto-${photo.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 4000);
      // Fire-and-forget notification so the photographer sees "download concluído"
      albumAPI.logDownload(album.code, 1, [photo.id], album.client || '');
    } catch (e) {
      // Fallback: open in new tab (user can save manually)
      window.open(photo.url, '_blank', 'noopener');
    }
  };

  const cancelMyOrders = async () => {
    if (!album) return;
    if (!window.confirm('Cancelar seus pedidos? As fotos voltarão a ficar bloqueadas e você poderá selecionar novamente.')) return;
    try {
      await albumAPI.cancelMyOrders(album.code);
      const updated = await albumAPI.get(album.code);
      setAlbum(updated);
      setSelected(new Set());
      setOrderPending(false);
      toast({ title: 'Pedidos cancelados', description: 'As fotos voltaram a ficar bloqueadas. Selecione novamente se desejar.' });
    } catch (e) {
      toast({ title: 'Erro ao cancelar', description: e?.response?.data?.detail || 'Tente novamente' });
    }
  };

  return (
    <div className="grain relative min-h-screen protected-root">
      {/* Header */}
      <header className="border-b border-[#1c1710] sticky top-0 bg-[#0b0906]/90 backdrop-blur z-30">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="brand-mark"><Camera size={16} strokeWidth={2.2} /></span>
            <span className="font-serif text-[18px] md:text-[20px] text-[#f2ecdf]">AXSELL</span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-[#a89e87]">
              <ShieldAlert size={13} className="gold-text" /> Proteção ativa
            </div>
            {paid ? (
              <>
                <button
                  onClick={cancelMyOrders}
                  className="btn-outline-dark px-3 py-2 rounded-md text-sm inline-flex items-center gap-2 text-red-300"
                  data-testid="cancel-my-orders-btn"
                  title="Cancelar meus pedidos"
                >
                  <RotateCcw size={14} />
                  <span className="hidden sm:inline">Cancelar</span>
                </button>
              </>
            ) : (
              <button
                disabled={!canCheckout}
                onClick={() => setCheckoutOpen(true)}
                data-testid="checkout-btn"
                className={`btn-gold px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2 ${!canCheckout && 'opacity-40 cursor-not-allowed'}`}>
                <ShoppingCart size={15} />
                R$ {(totals.total || 0).toFixed(2)}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Banner: pedido aguardando liberação */}
      {orderPending && !paid && (
        <div className="bg-amber-900/20 border-b border-amber-900/40">
          <div className="max-w-6xl mx-auto px-6 md:px-8 py-3 flex items-center justify-center gap-2 text-sm">
            <Clock size={16} className="text-amber-400 flex-shrink-0" />
            <span className="text-amber-200 text-center">
              <b>Pedido enviado.</b> Estamos confirmando o pagamento. Assim que liberado, as fotos aparecerão aqui.
            </span>
          </div>
        </div>
      )}
      {paid && (
        <div className="bg-[#d6af6c]/10 border-b border-[#d6af6c]/20">
          <div className="max-w-6xl mx-auto px-6 md:px-8 py-3 flex items-center justify-center gap-2 text-sm">
            <Check size={16} className="gold-text" />
            <span className="text-[#e6dcc7] text-center">
              <b>Download liberado!</b> Suas fotos estão prontas — clique no ícone dourado para baixar.
            </span>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-6 md:px-8 py-8 md:py-10">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.14em] text-[#a89e87]">Álbum • {album.code}</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#f2ecdf] mt-1">{album.title}</h1>
          <p className="text-sm text-[#a89e87] mt-1">{photos.length} fotos • Cliente: {album.client}</p>
        </div>

        {album.showMessages !== false && (album.welcomeMessage || album.instructionsMessage || album.thankYouMessage || album.deadlineMessage) && (
          <div className="card-glow rounded-xl p-5 md:p-6 mb-4 border-l-4 border-l-[#d6af6c]" data-testid="album-messages">
            {album.welcomeMessage && (
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-wider text-[#7d735e] mb-0.5">Boas-vindas</div>
                <p className="text-[#e6dcc7] leading-relaxed">{album.welcomeMessage}</p>
              </div>
            )}
            {album.instructionsMessage && (
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-wider text-[#7d735e] mb-0.5">Como escolher</div>
                <p className="text-sm text-[#c4bba5] leading-relaxed">{album.instructionsMessage}</p>
              </div>
            )}
            {album.deadlineMessage && (
              <div className="mb-3 bg-amber-900/20 border border-amber-500/30 rounded-md p-2.5">
                <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-0.5">Prazo</div>
                <p className="text-sm text-amber-100 leading-relaxed">{album.deadlineMessage}</p>
              </div>
            )}
            {album.thankYouMessage && paid && (
              <div className="mt-3 pt-3 border-t border-[#1c1710]">
                <div className="text-[10px] uppercase tracking-wider text-emerald-300 mb-0.5">Obrigada! por escolher meu trabalho! Qualquer dúvida, é só chamar no WhatsApp</div>
                <p className="text-sm text-emerald-100 leading-relaxed">{album.thankYouMessage}</p>
              </div>
            )}
          </div>
        )}

        {/* Purchase mode selector */}
        <div className="card-glow rounded-xl p-4 md:p-5 mb-4">
          <div className="text-xs uppercase tracking-[0.14em] text-[#a89e87] mb-3">Como você quer comprar</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <ModeCard active={mode === 'individual'} onClick={() => setMode('individual')} icon={ImageIcon} title="Por foto" desc={`R$ ${album.pricePerPhoto} cada`} />
            <ModeCard active={mode === 'package'} onClick={() => setMode('package')} icon={Package} title="Pacote" desc={`${album.packageCount} fotos por R$ ${album.packagePrice}${album.extraPhotoPrice ? ` (+R$ ${album.extraPhotoPrice}/extra)` : ''}`} />
            <ModeCard active={mode === 'full'} onClick={() => setMode('full')} icon={Grid3x3} title="Álbum completo" desc={`Todas as fotos por R$ ${album.albumPrice}`} />
          </div>
        </div>

        {/* Contador de fotos selecionadas */}
        {!paid && mode !== 'full' && (
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4 px-1">
            <div className="text-sm text-[#e6dcc7]">
              <span className="gold-text font-serif text-2xl">{selected.size}</span>
              <span className="text-[#a89e87] ml-2">
                {selected.size === 1 ? 'foto selecionada' : 'fotos selecionadas'}
                {mode === 'package' && album.packageCount && selected.size < album.packageCount && (
                  <span className="text-xs ml-2">
                    (faltam {album.packageCount - selected.size} para o pacote)
                  </span>
                )}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {selected.size > 0 && (
                <button
                  onClick={() => setSelected(new Set())}
                  className="text-xs text-[#a89e87] hover:text-red-400 underline"
                >
                  Limpar seleção
                </button>
              )}
              <div className="text-sm">
                <span className="text-[#a89e87]">Total:</span>{' '}
                <span className="gold-text font-serif text-2xl">R$ {(totals.total || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Photo/Video grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 md:gap-3">
          {photos.map((p, idx) => {
            const isSelected = mode === 'full' || paid || selected.has(p.id);
            const purchased = releasedIds.has(p.id);
            const isVideo = p.type === 'video';
            return (
              <div key={`${idx}-${p.id}`}
                className={`group relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                  isSelected ? 'border-[#d6af6c]' : 'border-transparent hover:border-[#3a2f20]'
                }`}>
                {isVideo ? (
                  <video
                    src={p.url}
                    muted
                    playsInline
                    preload="metadata"
                    onContextMenu={(e) => e.preventDefault()}
                    controlsList="nodownload noremoteplayback"
                    disablePictureInPicture
                    className={`protected-img w-full h-full object-cover transition-transform group-hover:scale-105 ${!purchased ? 'watermarked' : ''}`}
                  />
                ) : (
                  <img
                    src={p.url}
                    alt={p.name}
                    loading="lazy"
                    draggable="false"
                    onContextMenu={(e) => e.preventDefault()}
                    className={`protected-img w-full h-full object-cover transition-transform group-hover:scale-105 ${!purchased ? 'watermarked' : ''}`}
                  />
                )}
                {/* Marca d'água - só se não comprada */}
                {!purchased && <WatermarkOverlay logoUrl={album.ownerLogo} watermarkSize={album.watermarkSize} watermarkOpacity={album.watermarkOpacity} watermarkPosition={album.watermarkPosition} />}
                {/* Vídeo badge */}
                {isVideo && (
                  <div className="absolute top-2 left-2 bg-black/70 backdrop-blur text-white text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold flex items-center gap-1 pointer-events-none">
                    <Play size={10} strokeWidth={2.5} fill="currentColor" /> Vídeo
                  </div>
                )}

                {/* Botão AMPLIAR (sempre visível ao passar mouse) */}
                <button
                  onClick={(e) => { e.stopPropagation(); setLightboxIdx(idx); }}
                  className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all z-10"
                  title="Ampliar foto"
                >
                  <ZoomIn size={16} strokeWidth={2.2} />
                </button>

{/* Área de seleção: permitir selecionar novamente fotos não compradas */}
{!paid && mode !== 'full' && (
  <button
    type="button"
    onClick={() => toggle(p.id)}
    className="absolute inset-0 w-full h-full cursor-pointer"
    aria-label="Selecionar para comprar"
  />
)}

{/* Checkmark da seleção */}
{mode !== 'full' && !paid && (
  <div
    className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center ${
      selected.has(p.id)
        ? 'bg-[#d6af6c] text-[#1a140a]'
        : 'bg-black/60 text-white/70 opacity-0 group-hover:opacity-100'
    }`}
  >
    <Check size={14} strokeWidth={3} />
  </div>
)}

                {purchased && (
                  <>
                    <div className="absolute bottom-2 left-2 text-[10px] uppercase tracking-wider bg-[#d6af6c] text-[#1a140a] px-2 py-0.5 rounded font-semibold pointer-events-none">
                      Comprada
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); downloadPhoto(p); }}
                      className="absolute top-2 right-2 w-9 h-9 rounded-full bg-[#d6af6c] hover:bg-[#e5c07b] text-[#1a140a] flex items-center justify-center shadow-lg transition-transform hover:scale-105 z-20"
                      title="Baixar"
                    >
                      <Download size={16} strokeWidth={2.4} />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 text-xs text-[#7d735e] flex items-center gap-2">
          <ShieldAlert size={13} className="gold-text" />
          {paid
            ? 'Suas fotos compradas estão sem marca d’água. Toque no ícone dourado de cada foto para baixar.'
            : 'Fotos protegidas contra cópia. Print, download e captura de tela estão bloqueados até a compra.'}
        </div>
      </main>

      {checkoutOpen && (
        <CheckoutModal
          album={album}
          pixKey={pixKey}
          owner={owner}
          total={totals.total}
          count={mode === 'full' ? photos.length : totals.count}
          mode={mode}
          paid={paid}
          onPay={confirmPayment}
          onClose={() => setCheckoutOpen(false)}
        />
      )}

      {lightboxIdx !== null && (
        <AlbumLightbox
          album={album}
          index={lightboxIdx}
          purchased={releasedIds.has(photos[lightboxIdx]?.id)}
          selected={selected.has(photos[lightboxIdx]?.id)}
          canSelect={!paid && mode !== 'full'}
          onSelect={() => toggle(photos[lightboxIdx].id)}
          onDownload={() => downloadPhoto(photos[lightboxIdx])}
          onClose={() => setLightboxIdx(null)}
          onPrev={() => setLightboxIdx(i => (i - 1 + photos.length) % photos.length)}
          onNext={() => setLightboxIdx(i => (i + 1) % photos.length)}
          attackDetected={attackDetected}
        />
      )}
    </div>
  );
}

function ModeCard({ active, onClick, icon: Icon, title, desc }) {
  return (
    <button onClick={onClick}
      className={`text-left rounded-lg p-4 border transition-all ${
        active ? 'border-[#d6af6c] bg-[#1a140c]' : 'border-[#2a2318] bg-[#12100b] hover:border-[#3a2f20]'
      }`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={16} className={active ? 'gold-text' : 'text-[#7d735e]'} />
        <span className="font-medium text-[#f2ecdf] text-sm">{title}</span>
      </div>
      <div className="text-xs text-[#a89e87]">{desc}</div>
    </button>
  );
}

function CheckoutModal({ album, pixKey, owner, total, count, mode, paid, onPay, onClose }) {
  const { toast } = useToast();
  const hasPix = Boolean(pixKey);
  const payload = useMemo(() => hasPix ? buildPixPayload({
    key: pixKey,
    amount: total,
    merchantName: owner?.name || 'AXSELL',
    city: 'SAO PAULO',
    txid: album.code
  }) : '', [pixKey, total, owner, album.code, hasPix]);

  const copyPayload = () => {
    if (!payload) return;
    navigator.clipboard.writeText(payload);
    toast({ title: 'Código Pix copiado', description: 'Cole no seu app do banco.' });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="card-glow rounded-2xl p-6 md:p-8 max-w-md w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl text-[#f2ecdf]">{paid ? 'Pedido recebido' : 'Confirmar pedido'}</h3>
          <button onClick={onClose} className="text-[#7d735e] hover:text-[#f2ecdf]"><X size={20} /></button>
        </div>

        <div className="text-sm text-[#a89e87] mb-5">
          {mode === 'full' ? 'Todas as fotos do álbum' : `${count} foto${count !== 1 ? 's' : ''} selecionada${count !== 1 ? 's' : ''}`} • <span className="gold-text font-semibold">R$ {(total || 0).toFixed(2)}</span>
        </div>

        {!paid ? (
          !hasPix ? (
            <div data-testid="pix-not-configured" className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-md p-4 leading-relaxed">
              <b className="block mb-1">Pix ainda não configurado</b>
              O fotógrafo precisa cadastrar a chave Pix no painel antes de você conseguir pagar. Entre em contato com ele para prosseguir.
            </div>
          ) : (
          <>
            <div className="bg-white rounded-lg p-4 flex items-center justify-center mb-4">
              <QRCodeSVG value={payload} size={220} includeMargin={false} level="M" data-testid="pix-qr-code" />
            </div>

            <div className="bg-[#12100b] border border-[#2a2318] rounded-md p-3 mb-3">
              <div className="text-xs text-[#7d735e] mb-1">Pix copia e cola</div>
              <div data-testid="pix-copia-cola" className="text-[11px] text-[#e6dcc7] break-all font-mono leading-relaxed">
                {payload}
              </div>
            </div>

            <button onClick={copyPayload} data-testid="copy-pix-btn" className="btn-outline-dark w-full py-2.5 rounded-md text-sm inline-flex items-center justify-center gap-2 mb-2">
              <Copy size={15} /> Copiar código Pix
            </button>

            <div className="text-xs text-[#7d735e] text-center my-3">
              Faça o Pix e clique abaixo para enviar seu pedido. O fotógrafo confirma o pagamento antes de liberar o download.
            </div>

            <button onClick={onPay} data-testid="pix-confirm-btn" className="btn-gold w-full py-3 rounded-md font-medium text-sm inline-flex items-center justify-center gap-2">
              <Check size={16} /> Já paguei — enviar pedido
            </button>
          </>
          )
        ) : (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-[#d6af6c] flex items-center justify-center mx-auto mb-4">
              <Check size={26} strokeWidth={3} className="text-[#1a140a]" />
            </div>
            <p className="text-[#f2ecdf] mb-2">Suas fotos foram liberadas.</p>
            <p className="text-sm text-[#a89e87] mb-6">A marca d’água foi removida das fotos compradas. Toque no botão dourado de cada foto para baixar individualmente em alta resolução.</p>
            <div className="flex justify-center">
              <button onClick={onClose} className="btn-gold px-5 py-2.5 rounded-md text-sm font-medium">Ver minhas fotos</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AlbumLightbox({ album, index, purchased, selected, canSelect, onSelect, onDownload, onClose, onPrev, onNext, attackDetected }) {
  const photos = album?.photos || [];
  const photo = photos[index];

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onNext();
      else if (e.key === 'ArrowLeft') onPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onNext, onPrev]);

  if (!photo) return null;
  const isVideo = photo.type === 'video';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center lightbox-guard"
      onClick={onClose}
      onContextMenu={(e) => e.preventDefault()}
    >
      <button data-testid="lightbox-close-btn" onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-sm z-10">
        <X size={20} />
      </button>

      <div className="absolute top-4 left-4 text-white/70 text-sm backdrop-blur-sm bg-black/40 px-3 py-1.5 rounded-full">
        {index + 1} / {photos.length}
      </div>

      {/* Anti-screenshot warning banner */}
      {!purchased && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
          <div className="bg-red-950/70 border border-red-500/40 text-red-100 text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full inline-flex items-center gap-2 backdrop-blur-sm">
            <Lock size={12} /> Print, download e captura bloqueados
          </div>
        </div>
      )}

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {purchased ? (
          <button onClick={(e) => { e.stopPropagation(); onDownload(); }} className="btn-gold px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2">
            <Download size={15} /> Baixar
          </button>
        ) : canSelect ? (
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(); }}
            className={`px-5 py-2.5 rounded-md text-sm font-semibold inline-flex items-center gap-2 transition-all ${
              selected
                ? 'bg-[#1a140a] text-[#d6af6c] border border-[#d6af6c]'
                : 'btn-gold'
            }`}
          >
            <Check size={15} strokeWidth={3} />
            {selected ? 'Selecionada — clique para tirar' : 'Selecionar esta foto'}
          </button>
        ) : (
          <div className="bg-[#d6af6c] text-[#1a140a] px-4 py-2 rounded-md text-sm font-semibold">
            Álbum completo comprado
          </div>
        )}
      </div>

      <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-sm z-10">
        <ChevronLeft size={22} />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-sm z-10">
        <ChevronRight size={22} />
      </button>

      <div className="relative max-w-[95vw] max-h-[85vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
        {isVideo ? (
          <video
            src={photo.url}
            controls={purchased}
            autoPlay={purchased}
            muted={!purchased}
            playsInline
            loop={!purchased}
            controlsList="nodownload noremoteplayback"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
            className="protected-img max-w-[95vw] max-h-[85vh] rounded-lg shadow-2xl"
          />
        ) : (
          <img
            src={photo.url}
            alt={photo.name}
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
            className="protected-img max-w-[95vw] max-h-[85vh] object-contain rounded-lg shadow-2xl select-none"
          />
        )}
        {!purchased && <WatermarkOverlay logoUrl={album.ownerLogo} dense />}
        {/* Dynamic identity watermark — helps track leaks even if a screenshot slips
            through OS-level protections that browsers cannot fully block. */}
        {!purchased && (
          <div aria-hidden className="absolute inset-0 pointer-events-none z-10 overflow-hidden select-none">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-white/25 text-[13px] md:text-[15px] font-mono tracking-wider rotate-[-24deg] leading-loose whitespace-nowrap">
                {(album.client || 'CLIENTE') + ' • ' + album.code + ' • ' + new Date().toLocaleString('pt-BR')}
              </div>
            </div>
            <div className="absolute bottom-3 left-3 text-[10px] text-white/40 bg-black/40 px-2 py-1 rounded backdrop-blur-sm">
              PROTEGIDO • {album.code} • {new Date().toLocaleDateString('pt-BR')}
            </div>
          </div>
        )}
        {/* Screenshot-attempt overlay — fully covers the photo when an attempt is detected */}
        {attackDetected && !purchased && (
          <div className="absolute inset-0 rounded-lg bg-black flex flex-col items-center justify-center text-center px-6 z-20 border-2 border-red-500/60 animate-pulse">
            <ShieldAlert size={48} className="text-red-400 mb-3" />
            <div className="text-red-100 text-lg font-bold uppercase tracking-wider">Captura bloqueada</div>
            <div className="text-red-200/70 text-sm mt-2 max-w-xs">
              Detectamos uma tentativa de print, cópia ou captura de tela. Faça o pagamento para liberar o download em alta resolução.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

